<template>
  <div class="user-invite-sender">
    <div class="" v-if="sentTags.length > 0">
      <div class="">
        <p v-for="sentTag in sentTags" v-bind:key="sentTag.id || sentTag.text" class="">
          <span
            class="invite-status-row-text col-sm-8 col-sm-offset-1"
            data-test="tis-sent-tag-email"
            >{{ sentTag.text }}</span
          >
          <span class="col-sm-2 send-status-column">
            <span
              class="invite-status label label-success"
              v-if="sentTag.inviteDone"
              data-test="tis-sent-tag-completed"
              >Completed</span
            >
            <span
              class="invite-status label label-warning"
              v-if="!sentTag.inviteDone"
              data-test="tis-sent-tag-sent"
              >Sent</span
            >
            <button
              v-if="
                !sentTag.inviteDone &&
                deleteUrl &&
                (tagToConfirmDeletion == null || tagToConfirmDeletion === sentTag)
              "
              class="btn btn-sm delete-button"
              :disabled="deleting"
              :class="{
                'btn-default': sentTag !== tagToConfirmDeletion,
                'btn-danger': sentTag === tagToConfirmDeletion,
              }"
              @click="deletePending(sentTag)"
              data-test="delete-pending-invite"
              type="button"
            >
              <i v-if="sentTag !== tagToConfirmDeletion" class="glyphicon glyphicon-trash"></i>
              <span
                v-if="sentTag === tagToConfirmDeletion"
                data-test="confirm-delete-pending-invite"
                >Sure?</span
              >
            </button>
            <button
              v-if="sentTag === tagToConfirmDeletion && !deleting"
              class="btn btn-sm delete-button btn-default"
              @click="cancelDelete()"
              type="button"
            >
              Cancel
            </button>
          </span>
        </p>
      </div>
    </div>
    <div class="">
      <form class="col-sm-8 col-sm-offset-1" v-on:submit.prevent="send">
        <div class="input-wrapper form-control form-control-lg">
          <input
            type="text"
            class=""
            placeholder="Enter an email address"
            v-model="tag"
            data-test="tis-email-input"
            aria-label="Enter an email address"
          />
        </div>
      </form>
      <div class="col-sm-2 col-xs-12 send-button">
        <button
          class="btn btn-lg btn-default-communication send-invites-button"
          :disabled="sending || v$.tag.$invalid"
          @click="send"
          data-test="tis-send-btn"
          type="button"
        >
          <span class="hidden-xs">
            {{ sending ? "Sending..." : " Send " }}
          </span>
          <span class="visible-xs-inline-block">
            {{ sending ? "..." : " Send " }}
          </span>
        </button>
      </div>
    </div>
    <div class="">
      <div class="col-sm-10 col-sm-offset-1">
        <p class="text-danger" v-if="sendError">
          There was a problem sending the invites. Please check your internet connection and try
          again.
        </p>
        <p class="text-danger" v-if="deleteError">
          There was a problem deleting the invite. Please check your internet connection and try
          again.
        </p>
        <p class="text-danger" v-if="nameError && selectedUserTags.length > 0">
          Sorry, we don't have an email address for {{ nameError }}.
        </p>
        <p class="text-danger" v-if="selectedUserTags.length >= 40">
          Can't send more than 40 invites at once.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { useVuelidate } from "@vuelidate/core";
import { email, required } from "@vuelidate/validators";
import { computed, ref, watch } from "vue";
import UserTag from "../user-tag";
import { EmailRegEx } from "./useInviteControlBase";

const props = withDefaults(
  defineProps<{
    userTags: UserTag[];
    sendUrl: string;
    deleteUrl: string;
  }>(),
  {
    userTags: () => [] as UserTag[],
  },
);

const emit = defineEmits([
  "previous-invites",
  "send-started",
  "delete-started",
  "delete-completed",
]);

const sendError = ref(false);
const nameError = ref<string | null>(null);
const sending = ref(false);
const deleting = ref(false);
const deleteError = ref(false);

const tagToConfirmDeletion = ref<UserTag | null>(null);

function isEmail(value: string): boolean {
  return EmailRegEx.test(value ? value.trim() : value);
}

watch(
  () => props.userTags,
  (value: UserTag[]) => {
    if (value.length) {
      emit("previous-invites");
    }
  },
);

const tag = ref("");

const rules = {
  tag: { required, email },
};

const state = {
  tag,
};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

const selectedUserTags = ref<UserTag[]>([]);

const sentTagValues = ref<UserTag[] | null>(null);

function markAsSent(sentTag: UserTag): void {
  sentTag.inviteSent = true;
  if (!sentTagValues.value!.some((t) => (t.id || t.text) === (sentTag.id || sentTag.text))) {
    sentTagValues.value!.push(sentTag);
  }
}

// Initialize sentTagValues based on props.userTags
function initializeSentTagValues() {
  if (!sentTagValues.value) {
    sentTagValues.value = [];
    props.userTags.filter((t) => t.inviteSent && t.text).forEach((t) => markAsSent(t));
  }
}

// Call the initialization function when component is created
initializeSentTagValues();

// Watch for changes in userTags and update sentTagValues accordingly
watch(
  () => props.userTags,
  (newTags) => {
    sentTagValues.value = [];
    newTags.filter((t) => t.inviteSent && t.text).forEach((t) => markAsSent(t));
  },
  { immediate: true },
);

const sentTags = computed(() => {
  return sentTagValues.value ?? [];
});

function isValidTag(userTag: UserTag): boolean {
  return !!(userTag.id || isEmail(userTag.text));
}

function performSend(): void {
  if (sending.value) {
    return;
  }

  const tagsToSend: UserTag[] = [{ text: tag.value }];

  selectedUserTags.value.forEach((sut) => {
    sut.text = sut.text ? sut.text.trim() : sut.text;
    sut.email = sut.email ? sut.email.trim() : sut.email;

    if (isValidTag(sut)) {
      tagsToSend.push(sut);
    } else {
      nameError.value = sut.text;
    }
  });

  if (tagsToSend.length === 0) {
    return;
  }

  sending.value = true;
  emit("send-started");

  axios
    .post(
      props.sendUrl,
      tagsToSend.map((t) => t.id || t.text),
    )
    .then((e) => {
      sending.value = false;
      sendError.value = false;
      tag.value = "";
      tagsToSend.forEach((acceptedTag) => {
        markAsSent(acceptedTag);
      });

      selectedUserTags.value = selectedUserTags.value.filter((ut) => !ut.inviteSent); // Clear selectedUserTags
    })
    .catch((e) => {
      sending.value = false;
      sendError.value = true;
    });
}

function send(): void {
  window.setTimeout(() => performSend(), 1); // The reason for this timeout is the way tag creation works.
  // If we didn't have this timeout, if the last tag was still in "text" form, it wouldn't be sent.
}

function cancelDelete(): void {
  if (deleting.value || !props.deleteUrl) {
    return;
  }

  tagToConfirmDeletion.value = null;
}

function deletePending(tagToDelete: UserTag): void {
  if (deleting.value || !props.deleteUrl) {
    return;
  }

  const idOrEmail = tagToDelete.id || tagToDelete.email || tagToDelete.text;
  if (!idOrEmail) {
    return;
  }

  if (tagToConfirmDeletion.value !== tagToDelete) {
    tagToConfirmDeletion.value = tagToDelete;
    return;
  }

  deleting.value = true;
  deleteError.value = false;
  emit("delete-started");

  axios
    .delete(`${props.deleteUrl}?idOrEmail=${encodeURIComponent(idOrEmail)}`)
    .then((e) => {
      deleting.value = false;
      tagToConfirmDeletion.value = null;
      const indexOfDeletedSentTag = sentTags.value.indexOf(tagToDelete);
      if (indexOfDeletedSentTag >= 0) {
        sentTags.value.splice(indexOfDeletedSentTag, 1);
      }
      emit("delete-completed");
    })
    .catch((e) => {
      deleting.value = false;
      tagToConfirmDeletion.value = null;
      deleteError.value = true;
    });
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.col-xs-12 {
  @media (max-width: @screen-xs-max) {
    text-align: center;
  }
}

.send-invites-button {
  @media (max-width: @screen-xs-max) {
    margin-top: 10px;
  }
}

.stretch-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  min-width: 0;

  & > *:first-child {
    flex-grow: 1;
    min-width: 0;
    margin-right: 5px;
  }
}

.input-wrapper {
  height: 48px;
  line-height: 32px;
  width: 100% !important;
}

.input-wrapper input {
  border-width: 0 !important;
  font-size: 18px;
  outline: none !important;
  width: 100%;
  min-width: 100%;
}

.invite-status-row-text {
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 30px;
  margin-bottom: 20px;
}

.example-invite-row {
  color: #cccccc;
}

.invite-status {
  padding: 0.6em 0.9em;
}

/*
@media (max-width:768px) {
  .stretch-row {
    flex-direction: column;
    justify-content: flex-end;
  }

}

*/

.user-invite-sender-input {
  max-width: 435px !important; //Fix for IE11
}

.user-invite-sender .vue-tags-input {
  font-size: 18px !important;
  input {
    font-size: 18px !important;
  }

  .ti-tag {
    font-size: 18px !important;
    max-width: 95%;
    overflow: hidden;
  }
}

.invite-status-panel {
  max-height: 300px;
  overflow-y: auto;
}

.invite-status-row-text {
  text-overflow: ellipsis;
}

.send-status-column {
  white-space: nowrap;
}

.delete-button {
  margin-left: 10px;
}

.send-button {
  padding-left: 0;

  @media (max-width: @screen-xs-max) {
    padding-left: 15px;
  }
}
</style>
